import {
  parseToolCallMetadata,
  type ToolCall,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/toolCallMetadata';
import {
  isWorkspaceAgentCancelledToolResultContent,
  type ParsedToolResult,
  parseToolResultMetadata,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/toolResultMetadata';
import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import {
  type WorkspaceAgentRunOutcome,
  type WorkspaceAgentRunRecord,
  type WorkspaceAgentRunStatus,
} from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentRun';
import { getInferredRunIdForUserMessage } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getInferredRunIdForUserMessage';
import { getLatestWorkspaceAgentUserMessage } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getLatestWorkspaceAgentUserMessage';
import {
  getWorkspaceAgentRunSegmentBounds,
  isSequenceInWorkspaceAgentRunSegment,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentRunSegment';
import { hasWorkspaceAgentAssistantReplyAfterLatestUserMessage } from '~/components/v2/components/workspace/workspace-agent-panel/utils/hasWorkspaceAgentAssistantReplyAfterLatestUserMessage';
import { isWorkspaceAgentChoiceToolName } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentElicitationFollowUpHold';
import {
  parseWorkspaceAgentAskAnswer,
  type WorkspaceAgentAskAnswer,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/parseWorkspaceAgentAskAnswer';
import { readWorkspaceAgentAskQuestion } from '~/components/v2/components/workspace/workspace-agent-panel/utils/readWorkspaceAgentAskQuestion';
import { getWorkspaceAgentClientMessageId } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentMessageStore';

export type WorkspaceAgentToolStatus = 'working' | 'completed' | 'error' | 'awaitingApproval' | 'cancelled';

type ToolResultEntry = {
  content: string | null;
  parsed: ParsedToolResult;
};

export type WorkspaceAgentDisplayTool = {
  id: string;
  label: string;
  name: string;
  status: WorkspaceAgentToolStatus;
};

export type WorkspaceAgentDisplayRow =
  | { type: 'message'; id: string; message: WorkspaceAgentMessage }
  | {
      /** The user's answer to an ask tool, read from that tool's result once the dock has closed. */
      type: 'elicitationAnswer';
      id: string;
      answer: WorkspaceAgentAskAnswer;
      /** What the ask tool put to the user, from its call arguments. Null only for a call that carried no prompt. */
      question: string | null;
      sequence: number;
      toolCallId: string;
      toolName: string;
    }
  | {
      type: 'runGroup';
      id: string;
      runId: string;
      sequence: number;
      userMessageId: string;
      tools: WorkspaceAgentDisplayTool[];
      status: WorkspaceAgentRunStatus;
      outcome?: WorkspaceAgentRunOutcome;
      startedAt?: string;
      finishedAt?: string;
    };

export type GroupWorkspaceAgentMessagesForDisplayParams = {
  activeRunId?: string | null;
  /** Send has committed; show working before WSS `Run.started`. */
  isAwaitingRunStart?: boolean;
  messages: readonly WorkspaceAgentMessage[];
  pendingToolCallIds?: Set<string>;
  runsById?: Record<string, WorkspaceAgentRunRecord>;
};

type RunAccumulator = {
  finishedAt?: string;
  id: string;
  outcome?: WorkspaceAgentRunOutcome;
  runId: string;
  sequence: number;
  startedAt?: string;
  status: WorkspaceAgentRunStatus;
  tools: WorkspaceAgentDisplayTool[];
  userMessageId: string;
};

/** Places a loader-only run row after its user message in sequence order. */
const ACTIVE_RUN_SEQUENCE_OFFSET = 0.1;

/**
 * Anchors for a run-group row id. The first tool batch of a user turn is `<turn>::batch`;
 * a batch that follows an assistant message is `<turn>::batch-after-<assistant id>`.
 */
const FIRST_BATCH_ANCHOR = 'batch';
const BATCH_AFTER_ASSISTANT_ANCHOR_PREFIX = 'batch-after-';

const getToolStatus = ({
  pendingToolCallIds,
  result,
  toolCallId,
}: {
  pendingToolCallIds: Set<string>;
  result: ToolResultEntry | undefined;
  toolCallId: string;
}): WorkspaceAgentToolStatus => {
  if (!result) {
    if (pendingToolCallIds.has(toolCallId)) {
      return 'awaitingApproval';
    }

    return 'working';
  }

  const { content, parsed } = result;

  // A result arrived but we couldn't read its outcome, which is a failure to surface rather than
  // a call still in flight.
  if (parsed.outcome === 'unreadable') {
    return 'error';
  }

  if (parsed.metadata.success === undefined) {
    return 'working';
  }

  if (parsed.metadata.success) {
    return 'completed';
  }

  if (isWorkspaceAgentCancelledToolResultContent(content)) {
    return 'cancelled';
  }

  return 'error';
};

/** Optimistic rows render in the thread but do not anchor live run grouping. */
const isConfirmedUserMessage = (message: WorkspaceAgentMessage): boolean =>
  message.role === 'user' && !message.isPending && !message.id.startsWith('temp:');

type GetUnlinkedCancellingAnchorUserMessageIdParams = {
  cancellingRunId: string;
  isShowingAwaitingRun: boolean;
  latestUserMessageIdIncludingOptimistic: string | null;
  messages: WorkspaceAgentMessage[];
  rows: WorkspaceAgentDisplayRow[];
};

/** Unlinked cancelling drains stay on the interrupted turn unless the latest user is a follow-up send. */
const getUnlinkedCancellingAnchorUserMessageId = ({
  cancellingRunId,
  isShowingAwaitingRun,
  latestUserMessageIdIncludingOptimistic,
  messages,
  rows,
}: GetUnlinkedCancellingAnchorUserMessageIdParams): string | null => {
  const userMessages = messages.filter((message) => message.role === 'user');

  if (userMessages.length <= 1) {
    return userMessages.at(-1)?.id ?? null;
  }

  const latestUserMessage = userMessages.at(-1);
  const latestUserMessageId = latestUserMessage?.id ?? null;
  const latestTurnHasFollowUpRun =
    latestUserMessageId !== null &&
    ((isShowingAwaitingRun && latestUserMessageId === latestUserMessageIdIncludingOptimistic) ||
      (latestUserMessage !== undefined && !isConfirmedUserMessage(latestUserMessage)) ||
      rows.some(
        (row) =>
          row.type === 'runGroup' &&
          row.status === 'active' &&
          row.userMessageId === latestUserMessageId &&
          row.runId !== cancellingRunId,
      ));

  if (!latestTurnHasFollowUpRun) {
    return latestUserMessageId;
  }

  const priorUserMessage = userMessages.at(-2);

  if (priorUserMessage && isConfirmedUserMessage(priorUserMessage)) {
    return priorUserMessage.id;
  }

  const priorConfirmedUserMessage = [...userMessages]
    .slice(0, -1)
    .reverse()
    .find((message) => isConfirmedUserMessage(message));

  return priorConfirmedUserMessage?.id ?? userMessages[0]?.id ?? null;
};

const getRunRecordForUserMessage = ({
  runsById,
  userMessageId,
}: {
  runsById: Record<string, WorkspaceAgentRunRecord>;
  userMessageId: string;
}): WorkspaceAgentRunRecord | undefined =>
  Object.values(runsById).find((run) => run.triggeringUserMessageId === userMessageId);

/** Run is still in-flight or draining after cancel — not settled. */
const isLiveRunRecord = (run: WorkspaceAgentRunRecord | undefined): boolean =>
  run?.status === 'active' || run?.status === 'cancelling';

const hasInProgressWorkspaceAgentTools = (tools: WorkspaceAgentDisplayTool[]): boolean =>
  tools.some((tool) => tool.status === 'working');

const getRunIdForUserMessage = ({
  activeRunId,
  latestUserMessageIdIncludingOptimistic,
  runsById,
  userMessageId,
}: {
  activeRunId: string | null;
  latestUserMessageIdIncludingOptimistic: string | null;
  runsById: Record<string, WorkspaceAgentRunRecord>;
  userMessageId: string;
}): string => {
  const runRecord = getRunRecordForUserMessage({ runsById, userMessageId });

  if (runRecord) {
    return runRecord.runId;
  }

  const activeRun = activeRunId ? runsById[activeRunId] : undefined;

  if (
    activeRunId &&
    activeRun?.status === 'active' &&
    activeRun.triggeringUserMessageId === null &&
    userMessageId === latestUserMessageIdIncludingOptimistic
  ) {
    return activeRunId;
  }

  return getInferredRunIdForUserMessage(userMessageId);
};

const isActiveRunForUserMessage = ({
  activeRunId,
  isShowingAwaitingRun,
  isLatestRun,
  latestUserMessageIdIncludingOptimistic,
  runId,
  runsById,
  userMessageId,
}: {
  activeRunId: string | null;
  isLatestRun: boolean;
  isShowingAwaitingRun: boolean;
  latestUserMessageIdIncludingOptimistic: string | null;
  runId: string;
  runsById: Record<string, WorkspaceAgentRunRecord>;
  userMessageId: string;
}): boolean => {
  const runRecord = getRunRecordForUserMessage({ runsById, userMessageId });
  const activeRun = activeRunId ? runsById[activeRunId] : undefined;

  return (
    isLiveRunRecord(runRecord) ||
    runId === activeRunId ||
    (isShowingAwaitingRun && userMessageId === latestUserMessageIdIncludingOptimistic) ||
    (isLatestRun &&
      activeRun?.status === 'active' &&
      (activeRun.triggeringUserMessageId === userMessageId || activeRun.triggeringUserMessageId === null))
  );
};

const getRunStatus = ({
  activeRunId,
  forceFinished,
  hasInProgressTools = false,
  isShowingAwaitingRun,
  latestUserMessageIdIncludingOptimistic,
  runId,
  runsById,
  settleBatchDespiteCancelling = false,
  toolCount = 0,
  userMessageId,
}: {
  activeRunId: string | null;
  forceFinished: boolean;
  hasInProgressTools?: boolean;
  isShowingAwaitingRun: boolean;
  latestUserMessageIdIncludingOptimistic: string | null;
  runId: string;
  runsById: Record<string, WorkspaceAgentRunRecord>;
  settleBatchDespiteCancelling?: boolean;
  toolCount?: number;
  userMessageId: string;
}): WorkspaceAgentRunStatus => {
  const runRecord = runsById[runId];

  if (runRecord?.status === 'cancelling') {
    if (!forceFinished) {
      return 'cancelling';
    }

    if (settleBatchDespiteCancelling || toolCount > 0) {
      return 'finished';
    }

    return 'cancelling';
  }

  if (runRecord?.status === 'finished') {
    return 'finished';
  }

  // A toolCall with no result is still live even when WSS run state is missing (hydrate / missed
  // Run.started) or an assistant message force-settled the previous batch.
  if (hasInProgressTools) {
    return 'active';
  }

  if (forceFinished) {
    return 'finished';
  }

  if (runId === activeRunId) {
    return 'active';
  }

  if (isShowingAwaitingRun && userMessageId === latestUserMessageIdIncludingOptimistic) {
    return 'active';
  }

  return 'finished';
};

const appendToolsFromToolCalls = ({
  pendingToolCallIds,
  resultsByToolCallId,
  toolCalls,
  tools,
}: {
  pendingToolCallIds: Set<string>;
  resultsByToolCallId: Map<string, ToolResultEntry>;
  toolCalls: ToolCall[];
  tools: WorkspaceAgentDisplayTool[];
}): WorkspaceAgentDisplayTool[] => {
  const nextTools = [...tools];

  for (const toolCall of toolCalls) {
    const existingIndex = nextTools.findIndex((tool) => tool.id === toolCall.id);

    const nextTool = {
      id: toolCall.id,
      label: toolCall.description ?? toolCall.name,
      name: toolCall.name,
      status: getToolStatus({
        pendingToolCallIds,
        result: resultsByToolCallId.get(toolCall.id),
        toolCallId: toolCall.id,
      }),
    };

    if (existingIndex === -1) {
      nextTools.push(nextTool);
      continue;
    }

    nextTools[existingIndex] = nextTool;
  }

  return nextTools;
};

const getRunTimestamps = ({
  isLatestBatchInRun,
  runRecord,
  runSegmentMessages,
  userMessageId,
  messages,
}: {
  isLatestBatchInRun: boolean;
  messages: readonly WorkspaceAgentMessage[];
  runRecord: WorkspaceAgentRunRecord | undefined;
  runSegmentMessages: WorkspaceAgentMessage[];
  userMessageId: string;
}): { finishedAt?: string; startedAt?: string } => {
  if (runRecord && isLatestBatchInRun) {
    return {
      finishedAt: runRecord.finishedAt,
      startedAt: runRecord.startedAt,
    };
  }

  const firstToolCall = runSegmentMessages.find((message) => message.role === 'toolCall');
  const lastToolResult = [...runSegmentMessages].reverse().find((message) => message.role === 'toolResult');
  const userMessage = messages.find((message) => message.id === userMessageId);

  return {
    finishedAt: lastToolResult?.createdAt,
    startedAt: firstToolCall?.createdAt ?? userMessage?.createdAt,
  };
};

const buildRunGroupRow = (run: RunAccumulator): WorkspaceAgentDisplayRow => ({
  type: 'runGroup',
  id: run.id,
  runId: run.runId,
  sequence: run.sequence,
  userMessageId: run.userMessageId,
  tools: run.tools,
  status: run.status,
  outcome: run.outcome,
  startedAt: run.startedAt,
  finishedAt: run.finishedAt,
});

const getBatchId = (groupKey: string, batchAnchor: string): string => `${groupKey}::${batchAnchor}`;

/**
 * One id per run-group row for its whole lifecycle: the awaiting loader, `Run.started`, the
 * first tool call and the settled history row all build the same id. React keys the Lottie by
 * it and the virtualizer caches measured height by it, so any swap along that path remounts
 * the row and paints one frame at the run-group estimate. It is built only from what every
 * path can see — the user turn and, for a later batch, the assistant message it follows —
 * never from `runId` (placeholder `run-after-*` → WSS id) or the tool message id (unknown
 * while awaiting). `clientMessageId` is preferred over the row id so optimistic → confirmed
 * stays one key, while a follow-up send during cancel drain cannot steal the previous turn's
 * Lottie (a singleton "latest turn" id would).
 */
const getRunGroupBatchId = ({
  afterAssistantMessageId,
  userMessage,
  userMessageId,
}: {
  afterAssistantMessageId: string | null;
  userMessage: WorkspaceAgentMessage | undefined;
  userMessageId: string;
}): string => {
  if (afterAssistantMessageId) {
    return getBatchId(userMessageId, `${BATCH_AFTER_ASSISTANT_ANCHOR_PREFIX}${afterAssistantMessageId}`);
  }

  const clientMessageId = getWorkspaceAgentClientMessageId(userMessage?.metadata ?? null);

  return getBatchId(clientMessageId ?? userMessageId, FIRST_BATCH_ANCHOR);
};

const buildFinishedEmptyRunGroupRow = ({
  runRecord,
  userMessageId,
  sequence,
}: {
  runRecord: WorkspaceAgentRunRecord;
  sequence: number;
  userMessageId: string;
}): WorkspaceAgentDisplayRow => ({
  type: 'runGroup',
  id: getBatchId(runRecord.runId, userMessageId),
  runId: runRecord.runId,
  sequence,
  userMessageId,
  tools: [],
  status: 'finished',
  outcome: runRecord.outcome,
  startedAt: runRecord.startedAt,
  finishedAt: runRecord.finishedAt,
});

const hasRunGroupForRun = (rows: WorkspaceAgentDisplayRow[], runId: string): boolean =>
  rows.some((row) => row.type === 'runGroup' && row.runId === runId);

/** Only the latest assistant-split batch for a cancelling run should show drain UI. */
const settleEarlierCancellingRunGroups = (
  rows: WorkspaceAgentDisplayRow[],
  runsById: Record<string, WorkspaceAgentRunRecord>,
): void => {
  const cancellingRunIds = new Set(
    Object.values(runsById)
      .filter((run) => run.status === 'cancelling')
      .map((run) => run.runId),
  );

  for (const runId of cancellingRunIds) {
    const cancellingGroupIndices: number[] = [];

    for (let index = 0; index < rows.length; index++) {
      const row = rows[index];

      if (row.type === 'runGroup' && row.runId === runId && row.status === 'cancelling') {
        cancellingGroupIndices.push(index);
      }
    }

    if (cancellingGroupIndices.length <= 1) {
      continue;
    }

    for (let index = cancellingGroupIndices.length - 2; index >= 0; index--) {
      const rowIndex = cancellingGroupIndices[index];
      const row = rows[rowIndex];

      if (row.type !== 'runGroup') {
        continue;
      }

      if (row.tools.length === 0) {
        rows.splice(rowIndex, 1);

        for (let laterIndex = index + 1; laterIndex < cancellingGroupIndices.length; laterIndex++) {
          if (cancellingGroupIndices[laterIndex] > rowIndex) {
            cancellingGroupIndices[laterIndex] -= 1;
          }
        }

        continue;
      }

      rows[rowIndex] = { ...row, status: 'finished' };
    }
  }
};

const hasToolCallsForUserTurn = ({
  messages,
  userMessage,
}: {
  messages: readonly WorkspaceAgentMessage[];
  userMessage: WorkspaceAgentMessage;
}): boolean => {
  const bounds = getWorkspaceAgentRunSegmentBounds(messages, userMessage.id);

  if (!bounds) {
    return false;
  }

  return messages.some(
    (message) => message.role === 'toolCall' && isSequenceInWorkspaceAgentRunSegment(message.sequence, bounds),
  );
};

const hasLaterToolBatchesInSameTurn = ({
  anchorSequence,
  messages,
  userMessageId,
}: {
  anchorSequence: number;
  messages: readonly WorkspaceAgentMessage[];
  userMessageId: string;
}): boolean => {
  const bounds = getWorkspaceAgentRunSegmentBounds(messages, userMessageId);

  if (!bounds) {
    return false;
  }

  return messages.some(
    (message) =>
      message.role === 'toolCall' && isSequenceInWorkspaceAgentRunSegment(message.sequence, bounds, anchorSequence),
  );
};

/**
 * Collapses each user-message run into one or more rows. Tool batches split on assistant messages
 * so post-consent work stays near the latest reply instead of updating a group far above it.
 */
export const groupWorkspaceAgentMessagesForDisplay = ({
  activeRunId = null,
  isAwaitingRunStart = false,
  messages,
  pendingToolCallIds = new Set(),
  runsById = {},
}: GroupWorkspaceAgentMessagesForDisplayParams): WorkspaceAgentDisplayRow[] => {
  const sorted = [...messages].sort((left, right) => left.sequence - right.sequence);
  const resultsByToolCallId = new Map<string, ToolResultEntry>();

  for (const message of sorted) {
    if (message.role !== 'toolResult') {
      continue;
    }

    const parsed = parseToolResultMetadata(message.metadata);

    if (parsed) {
      const toolCallId = parsed.outcome === 'readable' ? parsed.metadata.toolCallId : parsed.toolCallId;

      resultsByToolCallId.set(toolCallId, { content: message.content, parsed });
    }
  }

  const rows: WorkspaceAgentDisplayRow[] = [];
  // Filled as tool calls are walked. Outlives the run so a result that lands after the run closed
  // still finds its question.
  const askQuestionsByToolCallId = new Map<string, string>();
  const latestUserMessageId = [...sorted].reverse().find((message) => isConfirmedUserMessage(message))?.id ?? null;
  const latestUserMessageIdIncludingOptimistic = getLatestWorkspaceAgentUserMessage(sorted)?.id ?? null;
  const liveRun = activeRunId === null ? undefined : runsById[activeRunId];
  // Send committed but `Run.started` may never arrive — still show working unless catch-up already has a reply.
  const isShowingAwaitingRun = isAwaitingRunStart && !hasWorkspaceAgentAssistantReplyAfterLatestUserMessage(sorted);

  let currentUserMessageId: string | null = null;
  let currentUserMessage: WorkspaceAgentMessage | undefined;
  let lastAssistantMessageIdInTurn: string | null = null;
  let currentRun: RunAccumulator | null = null;
  let currentRunRowIndex: number | null = null;
  let currentRunSegmentMessages: WorkspaceAgentMessage[] = [];

  const syncCurrentRunRow = () => {
    if (!currentRun) {
      return;
    }

    if (
      currentRunRowIndex === null ||
      currentRunRowIndex >= rows.length ||
      rows[currentRunRowIndex]?.id !== currentRun.id
    ) {
      currentRunRowIndex = rows.findIndex((row) => row.type === 'runGroup' && row.id === currentRun?.id);
    }

    if (currentRunRowIndex === -1) {
      rows.push(buildRunGroupRow(currentRun));
      currentRunRowIndex = rows.length - 1;
      return;
    }

    rows[currentRunRowIndex] = buildRunGroupRow(currentRun);
  };

  const removeCurrentRunRow = () => {
    if (!currentRun) {
      return;
    }

    if (
      currentRunRowIndex === null ||
      currentRunRowIndex >= rows.length ||
      rows[currentRunRowIndex]?.id !== currentRun.id
    ) {
      currentRunRowIndex = rows.findIndex((row) => row.type === 'runGroup' && row.id === currentRun?.id);
    }

    if (currentRunRowIndex !== -1) {
      rows.splice(currentRunRowIndex, 1);
    }

    currentRunRowIndex = null;
  };

  const finalizeCurrentRun = ({
    forceFinished = false,
    isLatestBatchInRun = false,
    settleBatchDespiteCancelling = false,
  } = {}) => {
    if (!currentRun) {
      return;
    }

    const runRecord =
      getRunRecordForUserMessage({ runsById, userMessageId: currentRun.userMessageId }) ?? runsById[currentRun.runId];
    const isStillActiveRun = currentRun.runId === activeRunId && runRecord?.status === 'active';

    if (forceFinished && currentRun.tools.length === 0 && isStillActiveRun) {
      removeCurrentRunRow();
      currentRun = null;
      currentRunSegmentMessages = [];
      return;
    }

    const timestamps = getRunTimestamps({
      isLatestBatchInRun,
      messages: sorted,
      runRecord,
      runSegmentMessages: currentRunSegmentMessages,
      userMessageId: currentRun.userMessageId,
    });

    currentRun = {
      ...currentRun,
      finishedAt: timestamps.finishedAt,
      outcome: isLatestBatchInRun ? runRecord?.outcome : undefined,
      startedAt: timestamps.startedAt,
      status: getRunStatus({
        activeRunId,
        forceFinished,
        hasInProgressTools: !settleBatchDespiteCancelling && hasInProgressWorkspaceAgentTools(currentRun.tools),
        isShowingAwaitingRun,
        latestUserMessageIdIncludingOptimistic,
        runId: currentRun.runId,
        runsById,
        settleBatchDespiteCancelling,
        toolCount: currentRun.tools.length,
        userMessageId: currentRun.userMessageId,
      }),
    };
    syncCurrentRunRow();
    currentRun = null;
    currentRunRowIndex = null;
    currentRunSegmentMessages = [];
  };

  const startEmptyActiveRunGroup = (userMessageId: string, sequence: number) => {
    const runId = getRunIdForUserMessage({
      activeRunId,
      latestUserMessageIdIncludingOptimistic,
      runsById,
      userMessageId,
    });
    const runRecord = getRunRecordForUserMessage({ runsById, userMessageId });

    currentRun = {
      id: getRunGroupBatchId({
        afterAssistantMessageId: lastAssistantMessageIdInTurn,
        userMessage: currentUserMessage,
        userMessageId,
      }),
      runId,
      sequence,
      userMessageId,
      tools: [],
      status: runRecord?.status === 'cancelling' ? 'cancelling' : 'active',
      startedAt: runRecord?.startedAt,
      finishedAt: runRecord?.finishedAt,
    };
    currentRunSegmentMessages = [];
    rows.push(buildRunGroupRow(currentRun));
    currentRunRowIndex = rows.length - 1;
  };

  /**
   * The answer reads as the user's turn. When the ask was the run's first step it sits above the
   * group, where the user gave it; after visible steps it follows them. The row does not need an
   * open run: a result that lands after the run closed still goes in at its own place in the thread.
   */
  const insertElicitationAnswerRow = (message: WorkspaceAgentMessage) => {
    if (!currentUserMessageId) {
      return;
    }

    const parsed = parseToolResultMetadata(message.metadata);

    if (!parsed) {
      return;
    }

    const toolCallId = parsed.outcome === 'readable' ? parsed.metadata.toolCallId : parsed.toolCallId;
    const tool = currentRun?.tools.find((candidate) => candidate.id === toolCallId);
    const toolName = parsed.outcome === 'readable' ? (parsed.metadata.toolName ?? tool?.name) : tool?.name;

    if (toolName === undefined || !isWorkspaceAgentChoiceToolName(toolName)) {
      return;
    }

    const answer = parseWorkspaceAgentAskAnswer({ content: message.content, toolName });

    if (!answer) {
      return;
    }

    const row: WorkspaceAgentDisplayRow = {
      type: 'elicitationAnswer',
      id: message.id,
      answer,
      question: askQuestionsByToolCallId.get(toolCallId) ?? null,
      sequence: message.sequence,
      toolCallId,
      toolName,
    };
    const askedBeforeAnyVisibleStep =
      currentRun !== null && !currentRun.tools.some((candidate) => !isWorkspaceAgentChoiceToolName(candidate.name));
    const runRowIndex = rows.findIndex((candidate) => candidate.type === 'runGroup' && candidate.id === currentRun?.id);

    if (askedBeforeAnyVisibleStep && runRowIndex !== -1) {
      rows.splice(runRowIndex, 0, row);
      currentRunRowIndex = runRowIndex + 1;
      return;
    }

    rows.push(row);
  };

  const ensureCurrentRun = (message: WorkspaceAgentMessage): RunAccumulator | null => {
    if (!currentUserMessageId) {
      return null;
    }

    if (currentRun) {
      return currentRun;
    }

    const runId = getRunIdForUserMessage({
      activeRunId,
      latestUserMessageIdIncludingOptimistic,
      runsById,
      userMessageId: currentUserMessageId,
    });
    const runRecord = getRunRecordForUserMessage({ runsById, userMessageId: currentUserMessageId });

    currentRun = {
      id: getRunGroupBatchId({
        afterAssistantMessageId: lastAssistantMessageIdInTurn,
        userMessage: currentUserMessage,
        userMessageId: currentUserMessageId,
      }),
      runId,
      sequence: message.sequence,
      userMessageId: currentUserMessageId,
      tools: [],
      status: getRunStatus({
        activeRunId,
        forceFinished: false,
        isShowingAwaitingRun,
        latestUserMessageIdIncludingOptimistic,
        runId,
        runsById,
        userMessageId: currentUserMessageId,
      }),
      startedAt: runRecord?.startedAt,
      finishedAt: runRecord?.finishedAt,
    };
    rows.push(buildRunGroupRow(currentRun));
    currentRunRowIndex = rows.length - 1;

    return currentRun;
  };

  for (const message of sorted) {
    if (message.role === 'user') {
      rows.push({ type: 'message', id: message.id, message });

      const isUnlinkedLiveRunOwner =
        message.id === latestUserMessageIdIncludingOptimistic &&
        isLiveRunRecord(liveRun) &&
        liveRun?.triggeringUserMessageId === null;
      const shouldAnchorLiveRunOnUserMessage =
        isConfirmedUserMessage(message) ||
        isUnlinkedLiveRunOwner ||
        (isShowingAwaitingRun && message.id === latestUserMessageIdIncludingOptimistic);

      if (!shouldAnchorLiveRunOnUserMessage) {
        continue;
      }

      finalizeCurrentRun({ forceFinished: true });
      currentUserMessageId = message.id;
      currentUserMessage = message;
      lastAssistantMessageIdInTurn = null;

      const runId = getRunIdForUserMessage({
        activeRunId,
        latestUserMessageIdIncludingOptimistic,
        runsById,
        userMessageId: message.id,
      });
      const runRecord = getRunRecordForUserMessage({ runsById, userMessageId: message.id });
      const isLatestRun = message.id === latestUserMessageIdIncludingOptimistic;

      if (
        !isActiveRunForUserMessage({
          activeRunId,
          isLatestRun,
          isShowingAwaitingRun,
          latestUserMessageIdIncludingOptimistic,
          runId,
          runsById,
          userMessageId: message.id,
        })
      ) {
        if (
          runRecord?.status === 'finished' &&
          runRecord.triggeringUserMessageId === message.id &&
          !hasRunGroupForRun(rows, runRecord.runId) &&
          !hasToolCallsForUserTurn({ messages: sorted, userMessage: message })
        ) {
          rows.push(
            buildFinishedEmptyRunGroupRow({
              runRecord,
              sequence: message.sequence + ACTIVE_RUN_SEQUENCE_OFFSET,
              userMessageId: message.id,
            }),
          );
        }

        continue;
      }

      startEmptyActiveRunGroup(message.id, message.sequence + ACTIVE_RUN_SEQUENCE_OFFSET);
      continue;
    }

    if (message.role === 'toolResult') {
      if (currentRun) {
        currentRunSegmentMessages.push(message);
      }

      insertElicitationAnswerRow(message);
      continue;
    }

    if (message.role === 'toolCall') {
      if (!currentUserMessageId) {
        continue;
      }

      currentRunSegmentMessages.push(message);
      const run = ensureCurrentRun(message);

      if (!run) {
        continue;
      }

      const toolCalls = parseToolCallMetadata(message.metadata);

      for (const toolCall of toolCalls) {
        const question = isWorkspaceAgentChoiceToolName(toolCall.name)
          ? readWorkspaceAgentAskQuestion(toolCall.arguments)
          : null;

        if (question !== null) {
          askQuestionsByToolCallId.set(toolCall.id, question);
        }
      }

      run.tools = appendToolsFromToolCalls({
        pendingToolCallIds,
        resultsByToolCallId,
        toolCalls,
        tools: run.tools,
      });
      run.status = getRunStatus({
        activeRunId,
        forceFinished: false,
        hasInProgressTools: hasInProgressWorkspaceAgentTools(run.tools),
        isShowingAwaitingRun,
        latestUserMessageIdIncludingOptimistic,
        runId: run.runId,
        runsById,
        userMessageId: run.userMessageId,
      });
      currentRun = run;
      syncCurrentRunRow();

      continue;
    }

    const shouldResumeActiveRunAfterAssistant = currentRun !== null && currentRun.tools.length > 0;
    const hasInProgressToolsInCurrentRun = currentRun !== null && hasInProgressWorkspaceAgentTools(currentRun.tools);
    const runRecordForCurrentTurn = currentUserMessageId
      ? getRunRecordForUserMessage({ runsById, userMessageId: currentUserMessageId })
      : undefined;
    const willResumeAfterAssistant =
      shouldResumeActiveRunAfterAssistant &&
      !hasInProgressToolsInCurrentRun &&
      currentUserMessageId !== null &&
      runRecordForCurrentTurn?.status === 'active' &&
      isActiveRunForUserMessage({
        activeRunId,
        isLatestRun: currentUserMessageId === latestUserMessageIdIncludingOptimistic,
        isShowingAwaitingRun,
        latestUserMessageIdIncludingOptimistic,
        runId: getRunIdForUserMessage({
          activeRunId,
          latestUserMessageIdIncludingOptimistic,
          runsById,
          userMessageId: currentUserMessageId,
        }),
        runsById,
        userMessageId: currentUserMessageId,
      });
    const settleBatchDespiteCancelling =
      shouldResumeActiveRunAfterAssistant &&
      currentUserMessageId !== null &&
      (willResumeAfterAssistant ||
        hasLaterToolBatchesInSameTurn({
          anchorSequence: message.sequence,
          messages: sorted,
          userMessageId: currentUserMessageId,
        }));

    finalizeCurrentRun({ forceFinished: true, settleBatchDespiteCancelling });
    rows.push({ type: 'message', id: message.id, message });
    lastAssistantMessageIdInTurn = message.id;

    if (willResumeAfterAssistant && currentUserMessageId) {
      startEmptyActiveRunGroup(currentUserMessageId, message.sequence + ACTIVE_RUN_SEQUENCE_OFFSET);
    }
  }

  finalizeCurrentRun({ isLatestBatchInRun: true });

  const injectPendingLiveRunGroup = (run: WorkspaceAgentRunRecord) => {
    // Unlinked live runs belong on the latest user turn, including optimistic — otherwise
    // `Run.started` would move the loader off the send that already showed "Working on it".
    const latestUserMessageIncludingOptimistic = getLatestWorkspaceAgentUserMessage(sorted);
    const anchorUserMessageId =
      run.triggeringUserMessageId ??
      (run.status === 'cancelling'
        ? getUnlinkedCancellingAnchorUserMessageId({
            cancellingRunId: run.runId,
            isShowingAwaitingRun,
            latestUserMessageIdIncludingOptimistic,
            messages: sorted,
            rows,
          })
        : (latestUserMessageIncludingOptimistic?.id ?? latestUserMessageId));

    const hasLiveRunGroup = rows.some(
      (row) =>
        row.type === 'runGroup' && row.runId === run.runId && (row.status === 'active' || row.status === 'cancelling'),
    );

    if (hasLiveRunGroup) {
      return;
    }

    const anchorMessage = anchorUserMessageId
      ? sorted.find((entry) => entry.id === anchorUserMessageId)
      : sorted.at(-1);
    const anchorSequence = anchorMessage?.sequence ?? Number.NEGATIVE_INFINITY;
    const hasAssistantAfterAnchor = sorted.some(
      (entry) => entry.role === 'assistant' && entry.sequence > anchorSequence,
    );

    if (hasAssistantAfterAnchor) {
      return;
    }

    const sequence = (anchorMessage?.sequence ?? sorted.length) + ACTIVE_RUN_SEQUENCE_OFFSET;
    const batchId = getRunGroupBatchId({
      afterAssistantMessageId: null,
      userMessage: anchorMessage,
      userMessageId: anchorUserMessageId ?? run.runId,
    });
    // A settled batch from an earlier run on the same turn already holds the turn's first-batch
    // id when nothing from the assistant came after it; this row cannot share a React key with it.
    const isBatchIdTaken = rows.some((row) => row.type === 'runGroup' && row.id === batchId);
    const runRow: WorkspaceAgentDisplayRow = {
      type: 'runGroup',
      id: isBatchIdTaken ? getBatchId(batchId, run.runId) : batchId,
      runId: run.runId,
      sequence,
      userMessageId: anchorUserMessageId ?? '',
      tools: [],
      status: run.status === 'cancelling' ? 'cancelling' : 'active',
      startedAt: run.startedAt,
    };
    const userRowIndex = anchorUserMessageId
      ? rows.findIndex((row) => row.type === 'message' && row.id === anchorUserMessageId)
      : -1;

    if (userRowIndex === -1) {
      rows.push(runRow);
    } else {
      rows.splice(userRowIndex + 1, 0, runRow);
    }
  };

  if (activeRunId) {
    const activeRun = runsById[activeRunId];

    if (activeRun?.status === 'active') {
      injectPendingLiveRunGroup(activeRun);
    }
  }

  for (const run of Object.values(runsById)) {
    if (run.status === 'cancelling') {
      injectPendingLiveRunGroup(run);
    }
  }

  settleEarlierCancellingRunGroups(rows, runsById);

  return rows;
};
